import { AdminSubModule, AutomationSubModule, C3SubModule, InjectionMoldingSubModule, MetalsSubModule, PlasticsSubModule, Printing3dSubModule, PrismCommonSubModule, PrismModule, SettingsSubModule } from "../enums/prism-module.enum"
import { UserAbilities } from "../models/user-abilities.model"


export const VisibleAbilityKey = 'visible'

// ============================================================================
// RULE: ALL NEW PERMISSIONS ARE FALSE BY DEFAULT - `visible` and every Can*
// flag. This is the starting set for a newly created user. The companion
// table, `appwrite-utility/empty-user-abilities.const.ts`, is the fill-in for
// EXISTING users whose saved permissions predate a module, so a new module
// must be `visible: false` in BOTH files or every current user gets it the
// moment it ships.
// ============================================================================
export const DefaultUserAbilities: UserAbilities = {
  [PrismModule.Printing3d]: {
    [VisibleAbilityKey]: false,
    [Printing3dSubModule.Products]: { [VisibleAbilityKey]: false, CanEdit: false },
  },
  [PrismModule.Plastics]: {
    [VisibleAbilityKey]: false,
    [PlasticsSubModule.Invoices]: { visible: false, CanEdit: false, CanRelease: false },
    [PlasticsSubModule.Orders]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanEditSkids: false,
      CanFlag: false,
      CanUseGeminiErrorDetection: false,
    },
    [PlasticsSubModule.OrderReview]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.Product]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanCreateTags: false,
      AlwaysSortAlphabetical: false,
    },
    [PlasticsSubModule.HardwareItem]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanCreateTags: false,
      CanViewInventoryTransactions: false,
      AlwaysSortAlphabetical: false,
    },
    [PlasticsSubModule.Fixture]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanCreateTags: false,
    },
    [PlasticsSubModule.PlasticsFinishingSchedule]: {
      [VisibleAbilityKey]: false,
      CanEditSchedule: false,
      CanManageFinishingTaskWarnings: false,
    },
    [PlasticsSubModule.PlasticsFinishingStation]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      canUseInventoryTransactionDialog: false,
    },
    [PlasticsSubModule.ProductionManager]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanReceiveNewProductInAwaitingPlanning: false,
      CanViewAndSuppressWarnings: false,
    },
    [PlasticsSubModule.TaskSearch]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.PlasticsReporting]: {
      [VisibleAbilityKey]: false,
      CanViewLaborByPerson: false,
    },
    [PlasticsSubModule.PlasticsCalendar]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.ShippingManager]: {
      [VisibleAbilityKey]: false,
      ReceivesSkidCreationNotifications: false,
      ReceivesShipmentStagedNotifications: false,
      CanEditShipments: false,
    },
    [PlasticsSubModule.RotoSchedule]: {
      [VisibleAbilityKey]: false,
      CanEditSchedule: false,
      CanCompleteScheduledProducts: false,
      CanEditK2Schedule: false,
      CanEditK10Schedule: false,
      CanEditK11Schedule: false,
      CanEditK5Schedule: false,
      CanEditL4Schedule: false,
      CanEditL6Schedule: false,
      CanPrintSchedules: false,
    },
    [PlasticsSubModule.RotoMachineOperation]: {
      [VisibleAbilityKey]: false,
      CanRecordArmCycle: false,
      CanUseSupervisorDialog: false,
      CanUseMaintenanceDialog: false,
      CanEdit: false,
    },
    [PlasticsSubModule.RotoSupervisor]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.Materials]: {
      [VisibleAbilityKey]: false,
      CanPerformInventoryTransaction: false,
      CanEdit: false
    },
    [PlasticsSubModule.MaterialMixing]: {
      [VisibleAbilityKey]: false,
      CanEditMixingOrder: false,
      CanReorder: false,
      CanUpdateQuantity: false,
    },
    [PlasticsSubModule.QualityControl]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesQualityAlertUpdatedOrCreatedNotification: false,
    },
    [PlasticsSubModule.MaterialMixingStation]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.QualityInspector]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesNewInspectionNotifications: false,
    },
    [PlasticsSubModule.PlasticsScrap]: {
      [VisibleAbilityKey]: false,
      CanModifyScrapReasons: false,
    },
    [PlasticsSubModule.ComplaintLog]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesUpdateNotifications: false,
      ReceivesScheduledProductNotifications: false,
    },
    [PlasticsSubModule.PlasticsShipping]: {
      [VisibleAbilityKey]: false,
      ReceivesShipmentReleasedNotifications: false,
      ReceivesDockInspectionCompletedNotifications: false,
    },
    [PlasticsSubModule.Forecasting]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.Molds]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.MoldCheckups]: {
      [VisibleAbilityKey]: false,
      CanPerformCheckups: false,
      ReceivesNeedsCheckupNotifications: false,
    },
    [PlasticsSubModule.GroceryStore]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.PlasticsStaticLabel]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Inventory]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanAddQuantities: false,
      CanRemoveQuantities: false,
      CanPrintHardwareItemLabel: false,
      CanUseMockBadgeScanButton: false,
      ReceivesComponentShipmentCompletionNotifications: false,
    },
    [PlasticsSubModule.InventoryManager]: {
      [VisibleAbilityKey]: false,
      CanOrder: false,
      CanReceiveNewProductInAwaitingPlanning: false,
    },
    [PlasticsSubModule.Settings]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Suncast]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesPriorityListChangeNotifications: false,
      ReceivesSnapshotCaptureNotifications: false,
    },
    [PlasticsSubModule.Clack]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Motionmaster]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Kolorcans]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Quoting]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.Pricing]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.PlasticsC3Coolers]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.MoldRepairRequests]: {
      [VisibleAbilityKey]: false,
      AdminEdit: false,
      ReceivesNewTaskNotifications: false,
    },
    [PlasticsSubModule.CustomerProcesses]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.HotList]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PlasticsSubModule.Metrics]: {
      [VisibleAbilityKey]: false,
    },
    [PlasticsSubModule.QuoteStudio]: {
      [VisibleAbilityKey]: false,
    },
  },
  [PrismModule.Automation]: {
    [VisibleAbilityKey]: false,
    [AutomationSubModule.FactoryNode]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [AutomationSubModule.Trends]: {
      [VisibleAbilityKey]: false,
    },
    [AutomationSubModule.RobotProgramming]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [AutomationSubModule.ClientDevices]: {
      [VisibleAbilityKey]: false,
      CanViewLogs: false,
      CanEdit: false
    },
    [AutomationSubModule.Dashboards]: {
      [VisibleAbilityKey]: false,
    },
    [AutomationSubModule.SoftwareTasks]: {
      [VisibleAbilityKey]: false,
      ReceivesCompletionNotifications: false,
    },
    [AutomationSubModule.TimeTracker]: {
      [VisibleAbilityKey]: false,
    },
    [AutomationSubModule.LabelPrinting]: {
      [VisibleAbilityKey]: false,
    },
    [AutomationSubModule.EngineeringUpdateMeeting]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanDeleteMembers: false,
    },
    [AutomationSubModule.KnowledgeBase]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [AutomationSubModule.Schematics]: {
      [VisibleAbilityKey]: false,
      CanEditSchematics: false,
      CanEditFootprints: false,
    },
    [AutomationSubModule.AutomationHardwareItem]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [AutomationSubModule.EngineeringProjects]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [AutomationSubModule.EngineeringProjectFiles]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
  },
  [PrismModule.Admin]: {
    [VisibleAbilityKey]: false,
    [AdminSubModule.ManageUsers]: {
      [VisibleAbilityKey]: false,
    },
    [AdminSubModule.Migrations]: {
      [VisibleAbilityKey]: false,
    },
    [AdminSubModule.Collections]: {
      [VisibleAbilityKey]: false
    },
    [AdminSubModule.OnsiteUtilities]: {
      [VisibleAbilityKey]: false,
    },
    [AdminSubModule.Utilities]: {
      [VisibleAbilityKey]: false,
    },
    [AdminSubModule.LabelPrinterGroups]: {
      [VisibleAbilityKey]: false,
    },
    [AdminSubModule.SvgPreview]: {
      [VisibleAbilityKey]: false
    },
    [AdminSubModule.Cribbage]: {
      [VisibleAbilityKey]: false
    },
    [AdminSubModule.CronJobs]: {
      [VisibleAbilityKey]: false
    },
    [AdminSubModule.DataserverManagement]: {
      [VisibleAbilityKey]: false
    },
  },
  [PrismModule.Settings]: {
    [VisibleAbilityKey]: false,
    [SettingsSubModule.ChangeLog]: {
      [VisibleAbilityKey]: false
    },
    [SettingsSubModule.Display]: {
      [VisibleAbilityKey]: false,
      CanViewOffNetwork: false,
      disableBackground: false,
    },
    [SettingsSubModule.BuildInfo]: {
      [VisibleAbilityKey]: false,
    },
  },
  [PrismModule.Metals]: {
    [VisibleAbilityKey]: false,
    [MetalsSubModule.ObsessedEngineering]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      canViewAdminPanels: false,
    },
    [MetalsSubModule.ProcessStation]: {
      [VisibleAbilityKey]: false,
      StationTypeListVisible: false,
    },
    [MetalsSubModule.Project]: {
      [VisibleAbilityKey]: false,
      canManuallyEditTaskStatus: false,
      canApplyProjectAsProductTemplate: false,
      canRevertCompletedTaskStatus: false,
    },
    [MetalsSubModule.MetalsHardwareItem]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanCreateTags: false,
    },
    [MetalsSubModule.MetalsTagGenerator]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.LaserProgramming]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.LaserOperator]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.LaserScheduling]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.Products]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanCreateTags: false,
    },
    [MetalsSubModule.Metaltrack]: {
      [VisibleAbilityKey]: false,
      CanEditJobs: false,
      CanEditSchedule: false,
      CanViewCutting: false,
      CanViewBending: false,
      CanViewPainting: false,
      CanDeleteCompletedJobs: false,
    },
    [MetalsSubModule.Settings]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.ProductionSchedule]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.Orders]: {
      [VisibleAbilityKey]: false,
      CanEdit: false
    },
    [MetalsSubModule.ProductionManager]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanReceiveNewProductInAwaitingPlanning: false,
    },
    [MetalsSubModule.MetalsQuoteRequest]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [MetalsSubModule.MetalsTools]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.CadViewer]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.SheetMetalStudio]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.Quoting]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [MetalsSubModule.AssemblyQuoting]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [MetalsSubModule.ToolRoom]: {
      [VisibleAbilityKey]: false,
    },
    [MetalsSubModule.ProductSetups]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [MetalsSubModule.ToolRoomMachines]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [MetalsSubModule.Inventory]: {
      [VisibleAbilityKey]: false,
    },
  },
  [PrismModule.PrismCommon]: {
    [VisibleAbilityKey]: false,
    [PrismCommonSubModule.Resources]: {
      [VisibleAbilityKey]: false,
      CanEdit: false
    },
    [PrismCommonSubModule.Procedures]: {
      [VisibleAbilityKey]: false,
      CanCreateTags: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.Assets]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanCreateTags: false,
    },
    [PrismCommonSubModule.Meetings]: {
      [VisibleAbilityKey]: false,
    },
    [PrismCommonSubModule.FreightCarriers]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.Customer]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanViewNotes: false,
    },
    [PrismCommonSubModule.Vendor]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.PurchaseRequests]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesWeeklyUnreadiedRequestNotifications: false,
    },
    [PrismCommonSubModule.Purchasing]: {
      [VisibleAbilityKey]: false,
      ReceivesPurchasingAgentNotifications: false,
    },
    [PrismCommonSubModule.PrismCommonReporting]: {
      [VisibleAbilityKey]: false,
    },
    [PrismCommonSubModule.PurchaseRequestReceiving]: {
      [VisibleAbilityKey]: false,
    },
    [PrismCommonSubModule.PurchasingDashboard]: {
      [VisibleAbilityKey]: false,
    },
    [PrismCommonSubModule.Contacts]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.SoftwareRequests]: {
      [VisibleAbilityKey]: true,
      CanEdit: false,
      ReceivesSoftwareRequestNotifications: false,
    },
    [PrismCommonSubModule.Maintenance]: {
      [VisibleAbilityKey]: false,
      AdminEdit: false,
      ReceivesNewTaskNotifications: false,
    },
    [PrismCommonSubModule.SmartJeff]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesNewProjectNotifications: false,
    },
    [PrismCommonSubModule.Projects]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.EmailGroups]: {
      [VisibleAbilityKey]: true,
    },
    [PrismCommonSubModule.DirectMessages]: {
      [VisibleAbilityKey]: false,
    },
    [PrismCommonSubModule.Finance]: {
      [VisibleAbilityKey]: false,
      CanViewBankBalance: false,
      CanViewMetalsFinance: false,
      CanViewPlasticsFinance: false,
      CanViewFinanceChart: false,
      CanViewOvertimeSummary: false,
      CanEditOvertimeSummary: false,
    },
    [PrismCommonSubModule.HumanResources]: {
      CanViewEmployeeRecords: false,
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.UpsShipments]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.Capitalization]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [PrismCommonSubModule.MoveRequests]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanWorkMoveQueue: false,
      ReceivesRequestNotifications: false,
    },
  },
  [PrismModule.C3]: {
    [VisibleAbilityKey]: false,
    [C3SubModule.C3Orders]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [C3SubModule.C3Product]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
    },
    [C3SubModule.C3HardwareItem]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransactions: false,
      ReceivesPrintDepartmentRequestNotifications: false,
    },
    [C3SubModule.C3Inventory]: {
      [VisibleAbilityKey]: false,
      CanPerformInventoryTransactions: false,
    },
    [C3SubModule.C3MoldedBodyCount]: {
      [VisibleAbilityKey]: false,
    },
    [C3SubModule.C3Assembly]: {
      [VisibleAbilityKey]: false,
    },
    [C3SubModule.C3StockLimits]: {
      [VisibleAbilityKey]: false,
      CanEditStockLimits: false,
    },
    [C3SubModule.C3Reporting]: {
      [VisibleAbilityKey]: false,
      CanViewInventoryThresholdReports: false,
      CanManageAssemblySessionReports: true,
    },
    [C3SubModule.StickerStudio]: {
      [VisibleAbilityKey]: false,
    },
    [C3SubModule.C3ClearanceInventory]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
    [C3SubModule.C3Graphics]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
  },
  [PrismModule.InjectionMolding]: {
    [VisibleAbilityKey]: false,
    [InjectionMoldingSubModule.Orders]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanViewDraftOrderedProducts: false,
      CanViewFutureInventoryOrders: false,
      CanReleaseOrderedProducts: false,
      CanSetReadyToShipOrderedProducts: false,
      CanCompleteOrderedProducts: false,
      CanHoldOrCancelOrderedProducts: false,
    },
    [InjectionMoldingSubModule.Products]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanViewInventoryTransactions: false,
    },
    [InjectionMoldingSubModule.HardwareItems]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanViewInventoryTransactions: false,
    },
    [InjectionMoldingSubModule.Materials]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      CanPerformInventoryTransaction: false,
      CanViewInventoryTransactions: false,
    },
    [InjectionMoldingSubModule.QualityControl]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
      ReceivesQualityAlertUpdatedOrCreatedNotification: false,
    },
    [InjectionMoldingSubModule.Molds]: {
      [VisibleAbilityKey]: false,
      CanEdit: false,
    },
  },
} as const
